<template>
  <div
    class="w-[95%] tablet-h:w-[740px] h-[50px] px-3 py-2.5 bg-gray-800 rounded-md flex items-center font-primary text-[14px] text-white z-100"
  >
    <CircularProgressIndicator
      v-if="uploadStore.uploadsInProgress.length"
      :percentage="avgPercentage"
      stroke-color="#00B3E6"
      text-color="white"
      class="mr-2.5 tablet-h:mr-3"
    />
    <div
      v-else-if="icon"
      class="w-[30px] h-[30px] flex items-center justify-center mr-2.5 tablet-h:mr-3"
    >
      <Icon :size="22" :name="icon" class="stroke-white" />
    </div>
    <div
      v-truncate
      class="flex flex-col tablet-h:flex-row tablet-h:items-center grow"
      :key="copy"
    >
      <div
        data-truncate-target
        class="flex items-center mr-2 grow tablet-h:grow-0"
      >
        {{ copy }}
      </div>
      <div
        v-if="uploadStore.uploadsInProgress.length"
        class="opacity-50 leading-[20px]"
      >
        {{ timeRemaining }}
      </div>
    </div>
    <div
      class="underline ml-auto mr-2.5 cursor-pointer"
      @click="handleViewDetailsClick"
    >
      View details
    </div>
    <Icon
      v-if="showCloseButton"
      :size="18"
      name="Plus"
      class="rotate-45 opacity-50 cursor-pointer stroke-white"
      @click="handleCloseClick"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import CircularProgressIndicator from '@/shared/components/CircularProgressIndicator.vue'
import { UploadEvent, useUploadStore } from '@/features/upload/store'
import { formatTimeLeft } from '@/shared/utils/datetime'
import { Icon, type IconName } from '@dacr/ui'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'

const dialog = useDialog()
const uploadStore = useUploadStore()

const hasAnyNonCanceledErrors = computed(() =>
  uploadStore.currentSessionUploads.filter(
    (upload) => upload.error && !upload.canceled
  )
)

watch(uploadStore, () => {
  if (
    uploadStore.finalUploadEvent !== UploadEvent.None &&
    !hasAnyNonCanceledErrors.value.length
  ) {
    setTimeout(() => {
      uploadStore.showUploadBar =
        uploadStore.finalUploadEvent === UploadEvent.None
    }, 7000)
  }
})

const icon = computed<IconName | null>(() => {
  if (uploadStore.finalUploadEvent === UploadEvent.Uploaded) return 'Check'
  if (
    uploadStore.finalUploadEvent === UploadEvent.Error ||
    uploadStore.finalUploadEvent === UploadEvent.Canceled
  ) {
    return 'Alert'
  }
  return null
})

const copy = computed(() => {
  if (uploadStore.finalUploadEvent === UploadEvent.Uploaded) {
    const uploadedFiles = uploadStore.currentSessionUploads.filter(
      (upload) => upload.percentage === 100 && !upload.error && !upload.canceled
    )
    if (uploadedFiles.length > 1) {
      return `Uploaded ${uploadedFiles.length} files`
    }
    if (uploadedFiles[0]) {
      return `Uploaded ${uploadedFiles[0].fileName}`
    }
    return 'Upload complete'
  }

  if (uploadStore.finalUploadEvent === UploadEvent.Canceled) {
    const canceledFiles = uploadStore.currentSessionUploads.filter(
      (upload) => upload.canceled
    )
    if (canceledFiles.length > 1) {
      return `${canceledFiles.length} uploads canceled`
    }
    return 'Upload cancelled'
  }

  if (uploadStore.finalUploadEvent === UploadEvent.Error) {
    const erroredUploads = uploadStore.currentSessionUploads.filter(
      (upload) => upload.error && !upload.canceled
    )
    if (erroredUploads.length > 1) {
      return `Unable to upload ${erroredUploads.length} files`
    }
    if (erroredUploads[0]) {
      return `Unable to upload ${erroredUploads[0].fileName}`
    }
    return 'Upload failed'
  }

  if (uploadStore.uploadsInProgress.length) {
    if (uploadStore.uploadsInProgress.length > 1) {
      return `Uploading ${uploadStore.uploadsInProgress.length} files`
    }
    return `Uploading ${uploadStore.uploadsInProgress[0].fileName}`
  }

  const processingUploads = uploadStore.currentSessionUploads.filter(
    (upload) => upload.percentage === 0 && !upload.error && !upload.canceled
  )
  if (processingUploads.length > 1) {
    return `Processing ${processingUploads.length} files`
  }
  if (processingUploads[0]) {
    return `Processing ${processingUploads[0].fileName}`
  }
  return ''
})

const avgPercentage = computed(() => {
  const totalPercentage = uploadStore.uploadsInProgress.reduce(
    (acc, upload) => (acc += upload.percentage),
    0
  )
  return Math.round(totalPercentage / uploadStore.uploadsInProgress.length)
})

const maxTimeRemaining = computed(() =>
  Math.max(...uploadStore.uploadsInProgress.map((upload) => upload.timeLeft))
)

const timeRemaining = computed(() => formatTimeLeft(maxTimeRemaining.value))

const showCloseButton = computed(() => {
  return hasAnyNonCanceledErrors.value.length
})

const handleViewDetailsClick = () => {
  dialog.open(Dialogs.UploadDetails, {
    props: { uploads: uploadStore.uploads },
  })
}

const handleCloseClick = () => {
  uploadStore.showUploadBar = false
}
</script>
